/** Toneart og tonenavn. Rene funksjoner uten avhengigheter. */

const KRYSS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLATT = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
const DUR_NAVN = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const MOLL_NAVN = ['Cm', 'C#m', 'Dm', 'Ebm', 'Em', 'Fm', 'F#m', 'Gm', 'G#m', 'Am', 'Bbm', 'Bm'];
const FLATTE = new Set(['F', 'Bb', 'Eb', 'Ab', 'Db', 'Dm', 'Gm', 'Cm', 'Fm', 'Bbm', 'Ebm']);
const GRUNNTONE: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const FORTEGN: Record<string, number> = { '': 0, '#': 1, b: -1 };

export interface Toneart {
  rot: number; // 0-11, C = 0
  slekt: 'dur' | 'moll';
  navn: string; // kanonisk: Bb, F#, Am, Bbm
  flatt: boolean; // toner uten fortegn i tall-akkorder og transponering skrives med b
}

/** Normaliserer toneart (B♭, F♯m, G major, C dur, A moll, g) til kanonisk skrivemåte (Bb, F#m, G, C, Am, Gm). Ukjent gir null. */
export function normaliserToneart(toneart: string | null | undefined): string | null {
  const m = /^([A-Ha-h])\s*([#b♯♭]?)\s*(m|min|minor|moll|maj|major|dur)?$/i.exec((toneart ?? '').trim());
  if (!m) return null;
  const suffiks = m[3];
  // Liten bokstav alene (g, f#) er moll; «Bm»/«bm» tolkes som moll, «BM» er ikke gyldig
  if (suffiks === 'M') return null;
  const moll = (suffiks !== undefined && /^(m|min|minor|moll)$/i.test(suffiks)) || (suffiks === undefined && m[1] === m[1].toLowerCase());
  const bokstav = m[1].toUpperCase() === 'H' ? 'B' : m[1].toUpperCase();
  const fortegn = m[2] === '♯' ? '#' : m[2] === '♭' ? 'b' : m[2];
  return `${bokstav}${fortegn}${moll ? 'm' : ''}`;
}

function lagToneart(rot: number, slekt: Toneart['slekt']): Toneart {
  const navn = (slekt === 'moll' ? MOLL_NAVN : DUR_NAVN)[rot];
  return { rot, slekt, navn, flatt: FLATTE.has(navn) };
}

/** Tolker en toneart til kanonisk form: A# blir Bb, Gb blir F#, A#m blir Bbm. Ukjent gir null. */
export function tolkToneart(toneart: string | null | undefined): Toneart | null {
  const m = /^([A-G])([#b]?)(m?)$/.exec(normaliserToneart(toneart) ?? '');
  if (!m) return null;
  return lagToneart((GRUNNTONE[m[1]] + FORTEGN[m[2]] + 12) % 12, m[3] ? 'moll' : 'dur');
}

/** Toneart fra feltene grunntone og type. Mangler typen, er den dur. Grunntonen kan ikke selv ha slekt (Am). */
export function tolkGrunntone(grunntone: string | null | undefined, type?: string | null): Toneart | null {
  const slekt = type ?? 'dur';
  if ((slekt !== 'dur' && slekt !== 'moll') || !/^[A-H][#b♯♭]?$/.test(grunntone ?? '')) return null;
  return tolkToneart(`${grunntone}${slekt === 'moll' ? 'm' : ''}`);
}

const tilOktav = (tone: number): number => ((tone % 12) + 12) % 12;

/** Flytter tonearten. Navn og flatt følger den nye tonearten. */
export function transponerToneart(toneart: Toneart, semitoner: number): Toneart {
  return lagToneart(tilOktav(toneart.rot + semitoner), toneart.slekt);
}

/** Navnet på tonen, med kryss eller b. Rot utenfor 0-11 regnes om. */
export function tonenavn(rot: number, flatt: boolean): string {
  return (flatt ? FLATT : KRYSS)[tilOktav(rot)];
}

/** Bb gir «Bb dur», F#m gir «F# moll». */
export function visToneart(toneart: Toneart): string {
  return toneart.slekt === 'moll' ? `${toneart.navn.slice(0, -1)} moll` : `${toneart.navn} dur`;
}

export interface Transponeringsvalg {
  semitoner: number;
  navn: string | null;
  tekst: string;
}

function lagValg(original: Toneart | null, semitoner: number): Transponeringsvalg {
  const navn = original && visToneart(transponerToneart(original, semitoner));
  const tall = `${semitoner > 0 ? '+' : ''}${semitoner}`;
  if (semitoner === 0) return { semitoner, navn, tekst: navn ? `Original (${navn})` : 'Original' };
  return { semitoner, navn, tekst: navn ? `${navn} (${tall})` : tall };
}

/** «Bb dur» fra feltene på sangen, så A# blir Bb. Ukjent toneart gir null. */
export function toneartNavn(grunntone: string | null | undefined, type?: string | null): string | null {
  const toneart = tolkGrunntone(grunntone, type);
  return toneart && visToneart(toneart);
}

/** «Original (Bb dur)», eller «Original» alene uten kjent toneart. */
export function originalTekst(grunntone: string | null | undefined, type?: string | null): string {
  return lagValg(tolkGrunntone(grunntone, type), 0).tekst;
}

/** Navnet på en lagret transponering regnes fra sangen, for det lagrede kan ha gammel stavemåte (A#). Uten kjent toneart brukes det lagrede. */
export function transponeringNavn(
  grunntone: string | null | undefined,
  type: string | null | undefined,
  semitoner: number,
  lagretNavn: string
): string {
  const toneart = tolkGrunntone(grunntone, type);
  return toneart ? visToneart(transponerToneart(toneart, semitoner)) : lagretNavn;
}

/** Valgene fra -6 til 6 halvtoner, i stigende rekkefølge. Uten toneart vises bare tallet. */
export function transponeringsvalg(original: Toneart | null): Transponeringsvalg[] {
  return Array.from({ length: 13 }, (_, i) => lagValg(original, i - 6));
}
